import { Component, useEffect, lazy, Suspense } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/Layout';
import { ProductLayout } from './components/ProductLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ScrollToTop } from './components/ScrollToTop';
import { ConstructionGate } from './components/ConstructionGate';

// Error boundary for lazy-loaded route chunks (handles stale deploys / network failures)
class ChunkErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, _info: ErrorInfo) {
    // Auto-retry once on chunk load failure (common after deploys)
    if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
      window.location.reload();
    }
  }
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

// Core pages -- loaded eagerly (small, always needed)
import {
  Landing,
  LoginPage,
  SSOCompletePage,
  ForgotPassword,
  ResetPassword,
  VerifyEmail,
  ProductDetail,
  AgentDetail,
  ContactSales,
  ContactUs,
  AcceptInvite,
  TeamManagement,
  OrgAdmin,
  ProductHub,
  Dashboard,
  Sites,
  SiteCreate,
  SiteDetail,
  RiskAnalysis,
  CapitalPlanning,
  Settings,
  EmployeeSignup,
  ClientSignup,
} from './pages';

import { useAuthStore } from './store/auth';
import { usePageTracking } from './hooks/usePageTracking';

// Polygon pages -- eager (contain Leaflet maps incompatible with Suspense)
import {
  PolygonDashboard, PolygonSites, PolygonSiteModeling,
  PolygonUpload, PolygonDocuments, CostAnalysisPage, CostCatalogSettings,
} from './pages/polygon';

// Other product pages -- lazy loaded (only fetched when route is visited)

const PulsePortfolio = lazy(() => import('./pages/pulse/PulsePortfolio').then(m => ({ default: m.PulsePortfolio })));
const PulseSites = lazy(() => import('./pages/pulse/PulseSites').then(m => ({ default: m.PulseSites })));
const PulseSiteDetail = lazy(() => import('./pages/pulse/PulseSiteDetail').then(m => ({ default: m.PulseSiteDetail })));

const PathfinderDashboard = lazy(() => import('./pages/pathfinder/PathfinderDashboard').then(m => ({ default: m.PathfinderDashboard })));
const PathfinderHistory = lazy(() => import('./pages/pathfinder/PathfinderHistory').then(m => ({ default: m.PathfinderHistory })));

// v1.2 — PreceptDashboard / PreceptAnalyze / PreceptResults / PreceptWizard /
// PreceptHistory removed. The standalone "classify a single batch" flow is
// superseded by PreceptComposer (planned-load workflow + four-gate engine).
const PreceptEP10 = lazy(() => import('./pages/precept/PreceptEP10').then(m => ({ default: m.PreceptEP10 })));
const PreceptWasteStreams = lazy(() => import('./pages/precept/PreceptWasteStreams').then(m => ({ default: m.PreceptWasteStreams })));
const PreceptWasteStreamDetail = lazy(() => import('./pages/precept/PreceptWasteStreamDetail').then(m => ({ default: m.PreceptWasteStreamDetail })));
const PreceptWasteStreamCreate = lazy(() => import('./pages/precept/PreceptWasteStreamCreate').then(m => ({ default: m.PreceptWasteStreamCreate })));
const PreceptManifests = lazy(() => import('./pages/precept/PreceptManifests').then(m => ({ default: m.PreceptManifests })));
const PreceptInjectionWells = lazy(() => import('./pages/precept/PreceptInjectionWells').then(m => ({ default: m.PreceptInjectionWells })));
const PreceptInjectionWellDetail = lazy(() => import('./pages/precept/PreceptInjectionWellDetail').then(m => ({ default: m.PreceptInjectionWellDetail })));
const PreceptManifestDetail = lazy(() => import('./pages/precept/PreceptManifestDetail').then(m => ({ default: m.PreceptManifestDetail })));
const PreceptManifestCreate = lazy(() => import('./pages/precept/PreceptManifestCreate').then(m => ({ default: m.PreceptManifestCreate })));
const PreceptOperators = lazy(() => import('./pages/precept/PreceptOperators').then(m => ({ default: m.PreceptOperators })));
const PreceptHaulers = lazy(() => import('./pages/precept/PreceptHaulers').then(m => ({ default: m.PreceptHaulers })));
const PreceptReceiverFacilities = lazy(() => import('./pages/precept/PreceptReceiverFacilities').then(m => ({ default: m.PreceptReceiverFacilities })));
const PreceptPermits = lazy(() => import('./pages/precept/PreceptPermits').then(m => ({ default: m.PreceptPermits })));
const PreceptFinancialAssurance = lazy(() => import('./pages/precept/PreceptFinancialAssurance').then(m => ({ default: m.PreceptFinancialAssurance })));
const PreceptClosurePlans = lazy(() => import('./pages/precept/PreceptClosurePlans').then(m => ({ default: m.PreceptClosurePlans })));
const PreceptReportFilings = lazy(() => import('./pages/precept/PreceptReportFilings').then(m => ({ default: m.PreceptReportFilings })));
const PreceptCorrectiveActions = lazy(() => import('./pages/precept/PreceptCorrectiveActions').then(m => ({ default: m.PreceptCorrectiveActions })));
const PreceptComplianceCalendar = lazy(() => import('./pages/precept/PreceptComplianceCalendar').then(m => ({ default: m.PreceptComplianceCalendar })));
const PreceptCockpit = lazy(() => import('./pages/precept/PreceptCockpit').then(m => ({ default: m.PreceptCockpit })));
const PreceptComposer = lazy(() => import('./pages/precept/PreceptComposer').then(m => ({ default: m.PreceptComposer })));
const PreceptPortfolioView = lazy(() => import('./pages/precept/PreceptPortfolioView').then(m => ({ default: m.PreceptPortfolioView })));

const PastureDashboard = lazy(() => import('./pages/pasture').then(m => ({ default: m.PastureDashboard })));
const PondDashboard = lazy(() => import('./pages/pond').then(m => ({ default: m.PondDashboard })));
const PermitDashboard = lazy(() => import('./pages/permit').then(m => ({ default: m.PermitDashboard })));
const PercolateDashboard = lazy(() => import('./pages/percolate').then(m => ({ default: m.PercolateDashboard })));
const PenDashboard = lazy(() => import('./pages/pen').then(m => ({ default: m.PenDashboard })));
const PathogenDashboard = lazy(() => import('./pages/pathogen').then(m => ({ default: m.PathogenDashboard })));
const PluviusDashboard = lazy(() => import('./pages/pluvius').then(m => ({ default: m.PluviusDashboard })));
const PaddockDashboard = lazy(() => import('./pages/paddock').then(m => ({ default: m.PaddockDashboard })));
const PlayaDashboard = lazy(() => import('./pages/playa').then(m => ({ default: m.PlayaDashboard })));
const ParticulateDashboard = lazy(() => import('./pages/particulate').then(m => ({ default: m.ParticulateDashboard })));
const PurgeDashboard = lazy(() => import('./pages/purge').then(m => ({ default: m.PurgeDashboard })));
const PremiseDashboard = lazy(() => import('./pages/premise').then(m => ({ default: m.PremiseDashboard })));
const PhreaticDashboard = lazy(() => import('./pages/phreatic').then(m => ({ default: m.PhreaticDashboard })));
const PhreaticSites = lazy(() => import('./pages/phreatic').then(m => ({ default: m.PhreaticSites })));
const PhreaticSiteModeling = lazy(() => import('./pages/phreatic').then(m => ({ default: m.PhreaticSiteModeling })));
const PhreaticCalculator = lazy(() => import('./pages/phreatic').then(m => ({ default: m.PhreaticCalculator })));
const PhreaticCommand = lazy(() => import('./pages/phreatic').then(m => ({ default: m.PhreaticCommand })));
const PhreaticBilling = lazy(() => import('./pages/phreatic').then(m => ({ default: m.PhreaticBilling })));
const PhreaticMermaid = lazy(() => import('./pages/phreatic').then(m => ({ default: m.PhreaticMermaid })));
const PhreaticBlueDiamond = lazy(() => import('./pages/phreatic').then(m => ({ default: m.PhreaticBlueDiamond })));

// DataVault -- eager (contains Leaflet map)
import { DataVault } from './pages/data-vault';
const DataVaultIntake = lazy(() => import('./pages/data-vault/intake/IntakePage').then(m => ({ default: m.IntakePage })));
const IntakeBatchDetail = lazy(() => import('./pages/data-vault/intake/BatchDetailPage').then(m => ({ default: m.BatchDetailPage })));
const FieldEntry = lazy(() => import('./pages/data-vault/intake/FieldEntryPage').then(m => ({ default: m.FieldEntryPage })));
const IntakeEdd = lazy(() => import('./pages/data-vault/intake/EddPage').then(m => ({ default: m.EddPage })));
const ValidationRulesPage = lazy(() => import('./pages/data-vault/intake/ValidationRulesPage'));
const QAQCReportPage = lazy(() => import('./pages/data-vault/intake/QAQCReportPage'));
const RegulatorySettingsPage = lazy(() => import('./pages/data-vault/settings/RegulatorySettingsPage'));
const QaReviewQueue = lazy(() => import('./pages/data-vault/QaReviewQueue'));
const BrandingSettingsPage = lazy(() => import('./pages/data-vault/settings/BrandingSettingsPage'));
const ReportsPage = lazy(() => import('./pages/data-vault/reports/ReportsPage').then(m => ({ default: m.ReportsPage })));
const ReportBuilderPage = lazy(() => import('./pages/data-vault/reports/ReportBuilderPage').then(m => ({ default: m.ReportBuilderPage })));
const ReportDetailPage = lazy(() => import('./pages/data-vault/reports/ReportDetailPage').then(m => ({ default: m.ReportDetailPage })));
const RegulatoryExportPage = lazy(() => import('./pages/data-vault/exports/RegulatoryExportPage').then(m => ({ default: m.RegulatoryExportPage })));
const ExportLandingPage = lazy(() => import('./pages/data-vault/exports/ExportLandingPage').then(m => ({ default: m.ExportLandingPage })));
const TemplateEditorPage = lazy(() => import('./pages/data-vault/exports/TemplateEditorPage').then(m => ({ default: m.TemplateEditorPage })));
const SamplingCalendarPage = lazy(() => import('./pages/data-vault/sampling/SamplingCalendarPage').then(m => ({ default: m.SamplingCalendarPage })));
const SamplingPlanPage = lazy(() => import('./pages/data-vault/sampling/SamplingPlanPage').then(m => ({ default: m.SamplingPlanPage })));
const SamplingEventPage = lazy(() => import('./pages/data-vault/sampling/SamplingEventPage').then(m => ({ default: m.SamplingEventPage })));
const FieldRecordForm = lazy(() => import('./pages/data-vault/sampling/FieldRecordForm').then(m => ({ default: m.FieldRecordForm })));
const AlertsPage = lazy(() => import('./pages/data-vault/notifications/NotificationCenter').then(m => ({ default: m.AlertsPage })));
const NotificationSettingsPage = lazy(() => import('./pages/data-vault/settings/NotificationSettingsPage').then(m => ({ default: m.NotificationSettingsPage })));
const AdminDashboard = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminDashboard })));
const ModelStatus = lazy(() => import('./pages/admin/ModelStatus').then(m => ({ default: m.ModelStatus })));
const DataImport = lazy(() => import('./pages/admin/DataImport').then(m => ({ default: m.DataImport })));
const FeedbackInbox = lazy(() => import('@/pages/admin/FeedbackInbox'));
const AdminAgents = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminAgents })));
const ReleaseNotes = lazy(() => import('./pages/admin/ReleaseNotes').then(m => ({ default: m.ReleaseNotes })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  const { isAuthenticated, checkAndClearExpiredSession } = useAuthStore();

  // Data flywheel: emit page_view / page_dwell beacons on every route change.
  usePageTracking();

  // Check for expired tokens on mount and clear stale sessions
  useEffect(() => {
    checkAndClearExpiredSession();
  }, [checkAndClearExpiredSession]);

  const LazyFallback = (
    <div className="flex items-center justify-center py-20">
      <div className="text-mono-500 text-sm tracking-widest animate-pulse">LOADING...</div>
    </div>
  );

  // Wrap a lazy component in its own Suspense boundary so sibling routes
  // (especially Leaflet maps) don't unmount/remount when a chunk loads.
  const S = ({ children }: { children: React.ReactNode }) => (
    <ChunkErrorBoundary fallback={LazyFallback}>
      <Suspense fallback={LazyFallback}>{children}</Suspense>
    </ChunkErrorBoundary>
  );


  return (
    <Routes>
      {/* Public routes */}
      {/* / is the marketing landing — served by this React SPA (the old Next.js argos-landing site is retired) */}
      <Route path="/" element={<Landing />} />
      {/* Marketplace pulled from the frontend (only 3 products — see Landing's
          product suite). Marketplace.tsx is retained for when the catalog
          grows. Redirect keeps any stale /marketplace links from 404ing. */}
      <Route path="/marketplace" element={<Navigate to="/" replace />} />
      <Route path="/products/:slug" element={<ProductDetail />} />
      <Route path="/agents/:slug" element={<AgentDetail />} />
      <Route path="/contact-sales" element={<ContactSales />} />
      <Route path="/contact" element={<ContactUs />} />
      {/* Unified login */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/sso-complete" element={<SSOCompletePage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />

      {/* Signup flows */}
      <Route path="/signup" element={<Navigate to="/client/signup" replace />} />
      <Route path="/client/signup" element={<ClientSignup />} />
      <Route path="/employee/signup" element={<EmployeeSignup />} />
      <Route path="/employee-signup" element={<Navigate to="/employee/signup" replace />} />

      {/* Legacy redirects → unified login/dashboard */}
      <Route path="/employee" element={<Navigate to="/login" replace />} />
      <Route path="/employee/login" element={<Navigate to="/login" replace />} />
      <Route path="/employee/dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route path="/client" element={<Navigate to="/login" replace />} />
      <Route path="/client/login" element={<Navigate to="/login" replace />} />
      <Route path="/client/dashboard" element={<Navigate to="/dashboard" replace />} />

      {/* Main product hub and legacy routes */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<ProductHub />} />
        <Route path="/sites" element={<Sites />} />
        <Route path="/sites/new" element={<SiteCreate />} />
        <Route path="/sites/:siteId" element={<SiteDetail />} />
        <Route path="/risk" element={<RiskAnalysis />} />
        <Route path="/capital" element={<CapitalPlanning />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/branding" element={<BrandingSettingsPage />} />
        <Route path="/team" element={<TeamManagement />} />
        <Route path="/account" element={<OrgAdmin />} />
        <Route path="/data-explorer" element={<Navigate to="/data-vault" replace />} />
        {/* Admin pages (superadmin role checked in components) */}
        <Route path="/admin/clients" element={<S><AdminDashboard /></S>} />
        <Route path="/admin/inquiries" element={<S><AdminDashboard /></S>} />
        <Route path="/admin/blue-diamond" element={<S><AdminDashboard /></S>} />
        <Route path="/admin/models" element={<S><ModelStatus /></S>} />
        <Route path="/admin/data-import" element={<S><DataImport /></S>} />
        <Route path="/feedback" element={<S><FeedbackInbox /></S>} />
        <Route path="/agents" element={<S><AdminAgents /></S>} />
        <Route path="/release-notes" element={<S><ReleaseNotes /></S>} />
      </Route>

      {/* Polygon product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/polygon" element={<Navigate to="/polygon/dashboard" replace />} />
        <Route path="/polygon/dashboard" element={<PolygonDashboard />} />
        <Route path="/polygon/upload" element={<PolygonUpload />} />
        <Route path="/polygon/sites" element={<PolygonSites />} />
        <Route path="/polygon/sites/:siteId" element={<PolygonSiteModeling />} />
        <Route path="/polygon/sites/:siteId/cost-analysis" element={<CostAnalysisPage />} />
        <Route path="/polygon/sites/:siteId/documents" element={<PolygonDocuments />} />
        <Route path="/polygon/analysis" element={<RiskAnalysis />} />
        <Route path="/polygon/capital" element={<CapitalPlanning />} />
        <Route path="/polygon/settings" element={<Settings />} />
        <Route path="/polygon/settings/costs" element={<CostCatalogSettings />} />
      </Route>

      {/* Data Vault product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/data-vault" element={<DataVault />} />
        <Route path="/data-vault/charts" element={<DataVault />} />
        <Route path="/data-vault/sites" element={<Sites />} />
        <Route path="/data-vault/sites/new" element={<SiteCreate />} />
        <Route path="/data-vault/sites/:siteId" element={<SiteDetail />} />
        <Route path="/data-vault/intake" element={<S><DataVaultIntake /></S>} />
        <Route path="/data-vault/intake/field-entry" element={<S><FieldEntry /></S>} />
        <Route path="/data-vault/intake/edd" element={<S><IntakeEdd /></S>} />
        <Route path="/data-vault/intake/validation-rules" element={<S><ValidationRulesPage /></S>} />
        <Route path="/data-vault/intake/:batchId" element={<S><IntakeBatchDetail /></S>} />
        <Route path="/data-vault/intake/:batchId/qaqc" element={<S><QAQCReportPage /></S>} />
        <Route path="/data-vault/reports" element={<S><ReportsPage /></S>} />
        <Route path="/data-vault/reports/new/:templateCode" element={<S><ReportBuilderPage /></S>} />
        <Route path="/data-vault/reports/:reportId" element={<S><ReportDetailPage /></S>} />
        <Route path="/data-vault/exports" element={<S><ExportLandingPage /></S>} />
        <Route path="/data-vault/exports/regulatory" element={<S><RegulatoryExportPage /></S>} />
        <Route path="/data-vault/exports/templates/:templateId/edit" element={<S><TemplateEditorPage /></S>} />
        <Route path="/data-vault/sampling" element={<S><SamplingCalendarPage /></S>} />
        <Route path="/data-vault/sampling/plans" element={<S><SamplingPlanPage /></S>} />
        <Route path="/data-vault/sampling/plans/:planId" element={<S><SamplingPlanPage /></S>} />
        <Route path="/data-vault/sampling/events/:eventId" element={<S><SamplingEventPage /></S>} />
        <Route path="/data-vault/sampling/events/:eventId/field/:wellId" element={<S><FieldRecordForm /></S>} />
        <Route path="/data-vault/alerts" element={<S><AlertsPage /></S>} />
        <Route path="/data-vault/settings/notifications" element={<S><NotificationSettingsPage /></S>} />
        <Route path="/data-vault/settings/regulatory" element={<S><RegulatorySettingsPage /></S>} />
        <Route path="/data-vault/qa-review" element={<S><QaReviewQueue /></S>} />
        <Route path="/datavault/settings" element={<Settings />} />
      </Route>

      {/* Pulse product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/pulse" element={<Navigate to="/pulse/portfolio" replace />} />
        <Route path="/pulse/portfolio" element={<S><PulsePortfolio /></S>} />
        <Route path="/pulse/sites" element={<S><PulseSites /></S>} />
        <Route path="/pulse/sites/:siteId" element={<S><PulseSiteDetail /></S>} />
        <Route path="/pulse/analytics" element={<RiskAnalysis />} />
        <Route path="/pulse/systems" element={<Settings />} />
        <Route path="/pulse/reports" element={<Settings />} />
        <Route path="/pulse/settings" element={<Settings />} />
      </Route>

      {/* Pathfinder product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/pathfinder" element={<Navigate to="/pathfinder/dashboard" replace />} />
        <Route path="/pathfinder/dashboard" element={<S><PathfinderDashboard /></S>} />
        <Route path="/pathfinder/history" element={<S><PathfinderHistory /></S>} />
        <Route path="/pathfinder/settings" element={<Settings />} />
      </Route>

      {/* PASTURE product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/pasture" element={<Navigate to="/pasture/dashboard" replace />} />
        <Route path="/pasture/dashboard" element={<S><PastureDashboard /></S>} />
        <Route path="/pasture/analyze" element={<S><PastureDashboard /></S>} />
        <Route path="/pasture/fields" element={<S><PastureDashboard /></S>} />
        <Route path="/pasture/settings" element={<Settings />} />
      </Route>

      {/* POND product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/pond" element={<Navigate to="/pond/dashboard" replace />} />
        <Route path="/pond/dashboard" element={<S><PondDashboard /></S>} />
        <Route path="/pond/analyze" element={<S><PondDashboard /></S>} />
        <Route path="/pond/lagoons" element={<S><PondDashboard /></S>} />
        <Route path="/pond/settings" element={<Settings />} />
      </Route>

      {/* PERMIT product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/permit" element={<Navigate to="/permit/dashboard" replace />} />
        <Route path="/permit/dashboard" element={<S><PermitDashboard /></S>} />
        <Route path="/permit/requirements" element={<S><PermitDashboard /></S>} />
        <Route path="/permit/documents" element={<S><PermitDashboard /></S>} />
        <Route path="/permit/settings" element={<Settings />} />
      </Route>

      {/* PERCOLATE product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/percolate" element={<Navigate to="/percolate/dashboard" replace />} />
        <Route path="/percolate/dashboard" element={<S><PercolateDashboard /></S>} />
        <Route path="/percolate/analyze" element={<S><PercolateDashboard /></S>} />
        <Route path="/percolate/sites" element={<S><PercolateDashboard /></S>} />
        <Route path="/percolate/settings" element={<Settings />} />
      </Route>

      {/* PEN product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/pen" element={<Navigate to="/pen/dashboard" replace />} />
        <Route path="/pen/dashboard" element={<S><PenDashboard /></S>} />
        <Route path="/pen/analyze" element={<S><PenDashboard /></S>} />
        <Route path="/pen/facilities" element={<S><PenDashboard /></S>} />
        <Route path="/pen/settings" element={<Settings />} />
      </Route>

      {/* PATHOGEN product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/pathogen" element={<Navigate to="/pathogen/dashboard" replace />} />
        <Route path="/pathogen/dashboard" element={<S><PathogenDashboard /></S>} />
        <Route path="/pathogen/analyze" element={<S><PathogenDashboard /></S>} />
        <Route path="/pathogen/sites" element={<S><PathogenDashboard /></S>} />
        <Route path="/pathogen/monitoring" element={<S><PathogenDashboard /></S>} />
        <Route path="/pathogen/settings" element={<Settings />} />
      </Route>

      {/* PLUVIUS product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/pluvius" element={<Navigate to="/pluvius/dashboard" replace />} />
        <Route path="/pluvius/dashboard" element={<S><PluviusDashboard /></S>} />
        <Route path="/pluvius/analyze" element={<S><PluviusDashboard /></S>} />
        <Route path="/pluvius/receptors" element={<S><PluviusDashboard /></S>} />
        <Route path="/pluvius/weather" element={<S><PluviusDashboard /></S>} />
        <Route path="/pluvius/settings" element={<Settings />} />
      </Route>

      {/* PADDOCK product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/paddock" element={<Navigate to="/paddock/dashboard" replace />} />
        <Route path="/paddock/dashboard" element={<S><PaddockDashboard /></S>} />
        <Route path="/paddock/analyze" element={<S><PaddockDashboard /></S>} />
        <Route path="/paddock/sites" element={<S><PaddockDashboard /></S>} />
        <Route path="/paddock/map" element={<S><PaddockDashboard /></S>} />
        <Route path="/paddock/settings" element={<Settings />} />
      </Route>

      {/* PLAYA product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/playa" element={<Navigate to="/playa/dashboard" replace />} />
        <Route path="/playa/dashboard" element={<S><PlayaDashboard /></S>} />
        <Route path="/playa/analyze" element={<S><PlayaDashboard /></S>} />
        <Route path="/playa/waterbodies" element={<S><PlayaDashboard /></S>} />
        <Route path="/playa/bmp" element={<S><PlayaDashboard /></S>} />
        <Route path="/playa/settings" element={<Settings />} />
      </Route>

      {/* PARTICULATE product routes */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/particulate" element={<Navigate to="/particulate/dashboard" replace />} />
        <Route path="/particulate/dashboard" element={<S><ParticulateDashboard /></S>} />
        <Route path="/particulate/analyze" element={<S><ParticulateDashboard /></S>} />
        <Route path="/particulate/receptors" element={<S><ParticulateDashboard /></S>} />
        <Route path="/particulate/controls" element={<S><ParticulateDashboard /></S>} />
        <Route path="/particulate/settings" element={<Settings />} />
      </Route>

      {/* PURGE - Brine Soil Remediation */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/purge" element={<Navigate to="/purge/dashboard" replace />} />
        <Route path="/purge/dashboard" element={<S><PurgeDashboard /></S>} />
        <Route path="/purge/analyze" element={<S><PurgeDashboard /></S>} />
        <Route path="/purge/sites" element={<S><PurgeDashboard /></S>} />
        <Route path="/purge/sites/:siteId" element={<S><PurgeDashboard /></S>} />
        <Route path="/purge/map" element={<S><PurgeDashboard /></S>} />
        <Route path="/purge/reports" element={<S><PurgeDashboard /></S>} />
        <Route path="/purge/settings" element={<Settings />} />
      </Route>

      {/* PRECEPT - Legacy login redirects to unified */}
      <Route path="/precept/login" element={<Navigate to="/login" replace />} />

      {/* PRECEPT - Multi-State Waste Characterization (Protected) */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        {/* v1.2 — all legacy classify entry points redirect to the
            Cockpit (the product's actual landing page per config.ts).
            "Enter Precept" should land on the command center, not the
            action workflow. */}
        <Route path="/precept" element={<Navigate to="/precept/cockpit" replace />} />
        <Route path="/precept/dashboard" element={<Navigate to="/precept/cockpit" replace />} />
        <Route path="/precept/wizard" element={<Navigate to="/precept/cockpit" replace />} />
        <Route path="/precept/analyze" element={<Navigate to="/precept/cockpit" replace />} />
        <Route path="/precept/results/:classificationId" element={<Navigate to="/precept/cockpit" replace />} />
        <Route path="/precept/ep10" element={<S><PreceptEP10 /></S>} />
        <Route path="/precept/waste-streams" element={<S><PreceptWasteStreams /></S>} />
        <Route path="/precept/waste-streams/new" element={<S><PreceptWasteStreamCreate /></S>} />
        <Route path="/precept/waste-streams/:wsId" element={<S><PreceptWasteStreamDetail /></S>} />
        <Route path="/precept/manifests" element={<S><PreceptManifests /></S>} />
        <Route path="/precept/manifests/new" element={<S><PreceptManifestCreate /></S>} />
        <Route path="/precept/manifests/:manifestId" element={<S><PreceptManifestDetail /></S>} />
        <Route path="/precept/receiver-facilities" element={<S><PreceptReceiverFacilities /></S>} />
        <Route path="/precept/injection-wells" element={<S><PreceptInjectionWells /></S>} />
        <Route path="/precept/injection-wells/:wellId" element={<S><PreceptInjectionWellDetail /></S>} />
        <Route path="/precept/compliance" element={<S><PreceptReportFilings /></S>} />
        <Route path="/precept/compliance/calendar" element={<S><PreceptComplianceCalendar /></S>} />
        <Route path="/precept/permits" element={<S><PreceptPermits /></S>} />
        <Route path="/precept/financial-assurance" element={<S><PreceptFinancialAssurance /></S>} />
        <Route path="/precept/closure-plans" element={<S><PreceptClosurePlans /></S>} />
        <Route path="/precept/corrective-actions" element={<S><PreceptCorrectiveActions /></S>} />
        <Route path="/precept/cockpit" element={<S><PreceptCockpit /></S>} />
        <Route path="/precept/composer" element={<S><PreceptComposer /></S>} />
        <Route path="/precept/seismic-map" element={<S><PreceptPortfolioView /></S>} />
        <Route path="/precept/renewals" element={<S><PreceptManifests /></S>} />
        <Route path="/precept/operators" element={<S><PreceptOperators /></S>} />
        <Route path="/precept/haulers" element={<S><PreceptHaulers /></S>} />
        <Route path="/precept/history" element={<Navigate to="/precept/cockpit" replace />} />
        <Route path="/precept/states" element={<Navigate to="/precept/cockpit" replace />} />
        <Route path="/precept/reference" element={<Navigate to="/precept/cockpit" replace />} />
        <Route path="/precept/settings" element={<Settings />} />
      </Route>

      {/* PREMISE - Environmental Site Assessment */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/premise" element={<Navigate to="/premise/dashboard" replace />} />
        <Route path="/premise/dashboard" element={<S><PremiseDashboard /></S>} />
        <Route path="/premise/assess" element={<S><PremiseDashboard /></S>} />
        <Route path="/premise/history" element={<S><PremiseDashboard /></S>} />
        <Route path="/premise/settings" element={<Settings />} />
      </Route>

      {/* PHREATIC - Groundwater Availability Certification */}
      <Route
        element={
          <ProtectedRoute>
            <ProductLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/phreatic" element={<Navigate to="/phreatic/command" replace />} />
        <Route path="/phreatic/command" element={<S><PhreaticCommand /></S>} />
        <Route path="/phreatic/sites" element={<S><PhreaticSites /></S>} />
        <Route path="/phreatic/sites/:siteId" element={<S><PhreaticSiteModeling /></S>} />
        <Route path="/phreatic/sites/:siteId/calculator" element={<S><PhreaticCalculator /></S>} />
        <Route path="/phreatic/dashboard" element={<Navigate to="/phreatic/sites" replace />} />
        <Route path="/phreatic/certify" element={<Navigate to="/phreatic/command" replace />} />
        <Route path="/phreatic/billing" element={<S><PhreaticBilling /></S>} />
        <Route path="/phreatic/mermaid" element={<S><PhreaticMermaid /></S>} />
        <Route path="/phreatic/blue-diamond" element={<S><PhreaticBlueDiamond /></S>} />
      </Route>

      {/* Legacy admin redirects */}
      <Route path="/admin" element={<Navigate to="/feedback" replace />} />
      <Route path="/admin/sales/clients" element={<Navigate to="/admin/clients" replace />} />
      <Route path="/admin/sales/inquiries" element={<Navigate to="/admin/inquiries" replace />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConstructionGate>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop>
            <AppRoutes />
          </ScrollToTop>
        </BrowserRouter>
      </ConstructionGate>
    </QueryClientProvider>
  );
}
